// Car audio: procedural engine, turbo, MGU-K whine, tyre squeal, kerb and
// off-track rumble, wind and impacts, mixed through a compressor and a
// safety limiter. On iPhone this is the only in-race feedback channel for
// kerbs, impacts and the grip limit (no Vibration API), so those layers are
// voiced in the 200 Hz–3 kHz band that phone speakers actually reproduce.
//
// Everything is driven from update(state, dt) once per rendered frame. All
// nodes are created up front; update() only sets AudioParam targets (no
// allocation), and one-shots (pops, blow-off, impacts) are envelopes on
// persistent gain nodes.

import {
  approach,
  clamp,
  ersGains,
  ersWhineHz,
  impactIntensity,
  newSurfaceVoice,
  overrunAmount,
  popRate,
  makeRng,
  safetyCurve,
  squealAmount,
  squealHz,
  surfaceSquealFactor,
  surfaceVoice,
  turboHz,
  turboTarget,
  windCutoffHz,
  windGain,
  type AudioSurface,
  type SquealCurve,
} from './mapping';
import { NoiseBank } from './noise';
import { Smooth, synthEngineVoice, type EngineVoice, type EngineVoiceFactory } from './engineVoice';
import { audioParams } from './params';

const DEG = Math.PI / 180;

export interface AudioState {
  /** Engine rpm (gearbox.rpm, 4,000–12,500). */
  rpm: number;
  /** Throttle pedal 0..1. */
  throttle: number;
  /** Current gear (gearbox.gear). */
  gear: number;
  /** Seconds since the last shift (gearbox.sinceShift). */
  sinceShift: number;
  /** Car speed, m/s. */
  speed: number;
  /** MGU-K power in kW: + deploying, − harvesting (PowerOutput.mguKW is in W: divide by 1000). */
  mguKW: number;
  /** Grip usage per axle, 0..1+ (car.gripUseFront/Rear). */
  gripFront: number;
  gripRear: number;
  /** Slip angles, rad (car.slipFront/Rear). */
  slipFront: number;
  slipRear: number;
  surface: AudioSurface;
  /** Largest speed lost to an impact since the last update, m/s (0 if none). */
  impact: number;
}

export interface CarAudioOptions {
  /** Where the mix goes (default ctx.destination). */
  destination?: AudioNode;
  /** Engine voice factory (default: procedural synth). */
  engine?: EngineVoiceFactory;
  /** Seed for the pop/crackle randomness (offline renders are reproducible). */
  seed?: number;
}

/** Fresh AudioState with neutral values; reuse one object per frame. */
export function idleAudioState(): AudioState {
  return {
    rpm: 4000, throttle: 0, gear: 1, sinceShift: 1, speed: 0, mguKW: 0,
    gripFront: 0, gripRear: 0, slipFront: 0, slipRear: 0, surface: 'track', impact: 0,
  };
}

function cancelHold(p: AudioParam, t: number): void {
  const withHold = p as AudioParam & { cancelAndHoldAtTime?: (t: number) => AudioParam };
  if (withHold.cancelAndHoldAtTime) withHold.cancelAndHoldAtTime(t);
  else p.cancelScheduledValues(t);
}

export class CarAudio {
  readonly noise: NoiseBank;
  readonly engine: EngineVoice;

  private readonly bus: GainNode;
  private readonly compressor: DynamicsCompressorNode;
  private readonly master: GainNode;
  private readonly safetyIn: GainNode;
  private readonly safety: WaveShaperNode;
  private readonly owned: AudioNode[] = [];
  private readonly oscillators: OscillatorNode[] = [];

  // Engine bus: voice → ignition cut → level.
  private readonly cut: GainNode;
  private readonly engineLevel: Smooth;
  // Overrun pops.
  private readonly popBand: BiquadFilterNode;
  private readonly popEnv: GainNode;
  private readonly popLevel: Smooth;
  // Turbo.
  private readonly turboWhistle: OscillatorNode;
  private readonly sWhistleHz: Smooth;
  private readonly sWhistle: Smooth;
  private readonly turboHiss: BiquadFilterNode;
  private readonly sHissHz: Smooth;
  private readonly sHiss: Smooth;
  private readonly bovBand: BiquadFilterNode;
  private readonly bovEnv: GainNode;
  private readonly turboLevel: Smooth;
  // MGU-K.
  private readonly ersDeployHz: Smooth;
  private readonly ersDeploy: Smooth;
  private readonly ersHarvestHz: Smooth;
  private readonly ersHarvestLpHz: Smooth;
  private readonly ersHarvest: Smooth;
  private readonly ersLevel: Smooth;
  // Tyres.
  private readonly sqFrontHz: Smooth;
  private readonly sqFront: Smooth;
  private readonly sqRearHz: Smooth;
  private readonly sqRear: Smooth;
  private readonly tyreLevel: Smooth;
  // Surface rumble.
  private readonly surfBandHz: Smooth;
  private readonly surfQ: Smooth;
  private readonly surfAmBase: Smooth;
  private readonly surfPulseHz: Smooth;
  private readonly surfPulseDepth: Smooth;
  private readonly surfRandDepth: Smooth;
  private readonly surfRandRate: Smooth;
  private readonly surfThump: Smooth;
  private readonly surfGain: Smooth;
  private readonly surfLevel: Smooth;
  // Wind.
  private readonly windLpHz: Smooth;
  private readonly wind: Smooth;
  private readonly windLevel: Smooth;
  // Impacts.
  private readonly impThump: OscillatorNode;
  private readonly impThumpEnv: GainNode;
  private readonly impBand: BiquadFilterNode;
  private readonly impCrunchEnv: GainNode;
  private readonly impactLevel: Smooth;
  private readonly masterGain: Smooth;

  // JS-side state.
  private readonly rng: () => number;
  private readonly sv = newSurfaceVoice();
  private readonly squealCurve: SquealCurve = { onset: 0.92, slipOnset: 0.1, slipFull: 0.2 };
  private load = 0;
  private spool = 0;
  private prevGear = 0;
  private prevSinceShift = 0;
  private sinceLift = 10;
  private bovArmed = false;
  private lastImpactT = -1;
  private lastImpactI = 0;
  private muted = false;
  private started = false;
  private disposed = false;

  constructor(
    readonly ctx: BaseAudioContext,
    opts: CarAudioOptions = {},
  ) {
    this.rng = makeRng(opts.seed ?? 1234);
    const g = (v = 1): GainNode => {
      const n = ctx.createGain();
      n.gain.value = v;
      this.owned.push(n);
      return n;
    };
    const f = (type: BiquadFilterType, hz: number, q = 0.707): BiquadFilterNode => {
      const n = ctx.createBiquadFilter();
      n.type = type;
      n.frequency.value = hz;
      n.Q.value = q;
      this.owned.push(n);
      return n;
    };
    const osc = (type: OscillatorType, hz: number): OscillatorNode => {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = hz;
      this.oscillators.push(o);
      return o;
    };

    this.noise = new NoiseBank(ctx);
    const white = this.noise.white;

    // ---- Master: bus → glue compressor → master gain → safety soft clipper.
    this.bus = g(1);
    this.compressor = ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -16;
    this.compressor.knee.value = 10;
    this.compressor.ratio.value = 4;
    this.compressor.attack.value = 0.004;
    this.compressor.release.value = 0.18;
    this.owned.push(this.compressor);
    this.master = g(0);
    this.safetyIn = g(0.5); // safetyCurve maps ±2 → ±1 domain
    this.safety = ctx.createWaveShaper();
    this.safety.curve = safetyCurve(4096, 0.8, 0.97, 2);
    this.owned.push(this.safety);
    this.bus.connect(this.compressor).connect(this.master).connect(this.safetyIn).connect(this.safety);
    this.safety.connect(opts.destination ?? ctx.destination);
    this.masterGain = new Smooth(this.master.gain, 0);

    // ---- Engine.
    this.engine = (opts.engine ?? synthEngineVoice)(ctx, this.noise);
    this.cut = g(1);
    const engineOut = g(0);
    this.engine.output.connect(this.cut).connect(engineOut).connect(this.bus);
    this.engineLevel = new Smooth(engineOut.gain, 0);

    // Overrun pops / shift cracks: noise burst → band → envelope.
    this.popBand = f('bandpass', 1100, 0.7);
    this.popEnv = g(0);
    const popOut = g(0);
    white.connect(this.popBand).connect(this.popEnv).connect(popOut).connect(this.bus);
    this.popLevel = new Smooth(popOut.gain, 0);

    // ---- Turbo: whistle sine + hiss band, blow-off flutter.
    const turboOut = g(0);
    turboOut.connect(this.bus);
    this.turboLevel = new Smooth(turboOut.gain, 0);
    this.turboWhistle = osc('sine', 2000);
    const whistleGain = g(0);
    this.turboWhistle.connect(whistleGain).connect(turboOut);
    this.sWhistleHz = new Smooth(this.turboWhistle.frequency, 2000);
    this.sWhistle = new Smooth(whistleGain.gain, 0);
    this.turboHiss = f('bandpass', 2000, 7);
    const hissGain = g(0);
    white.connect(this.turboHiss).connect(hissGain).connect(turboOut);
    this.sHissHz = new Smooth(this.turboHiss.frequency, 2000);
    this.sHiss = new Smooth(hissGain.gain, 0);
    this.bovBand = f('bandpass', 1200, 1.6);
    const bovAm = g(0.5);
    const flutter = osc('square', 23);
    const flutterDepth = g(0.5);
    flutter.connect(flutterDepth).connect(bovAm.gain);
    this.bovEnv = g(0);
    white.connect(this.bovBand).connect(bovAm).connect(this.bovEnv).connect(turboOut);

    // ---- MGU-K: deploy whine (bright, clean) and harvest whirr (lower, softer, tremolo).
    const ersOut = g(0);
    ersOut.connect(this.bus);
    this.ersLevel = new Smooth(ersOut.gain, 0);
    const deployOsc = ctx.createOscillator();
    deployOsc.setPeriodicWave(
      ctx.createPeriodicWave(new Float32Array([0, 0, 0, 0, 0, 0]), new Float32Array([0, 1, 0.12, 0.22, 0, 0.07])),
    );
    this.oscillators.push(deployOsc);
    const deployGain = g(0);
    deployOsc.connect(deployGain).connect(ersOut);
    this.ersDeployHz = new Smooth(deployOsc.frequency, 100);
    this.ersDeploy = new Smooth(deployGain.gain, 0);
    const harvestOsc = osc('triangle', 80);
    const harvestLp = f('lowpass', 800, 2);
    const harvestAm = g(0.7);
    const tremolo = osc('sine', 14);
    const tremoloDepth = g(0.3);
    tremolo.connect(tremoloDepth).connect(harvestAm.gain);
    const harvestGain = g(0);
    harvestOsc.connect(harvestLp).connect(harvestAm).connect(harvestGain).connect(ersOut);
    this.ersHarvestHz = new Smooth(harvestOsc.frequency, 80);
    this.ersHarvestLpHz = new Smooth(harvestLp.frequency, 800);
    this.ersHarvest = new Smooth(harvestGain.gain, 0);

    // ---- Tyres: two narrow noise bands (front higher, rear lower) with warble.
    const tyreOut = g(0);
    tyreOut.connect(this.bus);
    this.tyreLevel = new Smooth(tyreOut.gain, 0);
    const bpF = f('bandpass', 1100, 12);
    const bpR = f('bandpass', 900, 12);
    const warble = g(45); // cents
    this.noise.jitter.connect(warble);
    warble.connect(bpF.detune);
    warble.connect(bpR.detune);
    const gF = g(0);
    const gR = g(0);
    white.connect(bpF).connect(gF).connect(tyreOut);
    white.connect(bpR).connect(gR).connect(tyreOut);
    this.sqFrontHz = new Smooth(bpF.frequency, 1100);
    this.sqRearHz = new Smooth(bpR.frequency, 900);
    this.sqFront = new Smooth(gF.gain, 0);
    this.sqRear = new Smooth(gR.gain, 0);

    // ---- Surface: band-passed noise, AM by a ridge pulse train and random clumps,
    // plus a low thump tone at the ridge rate.
    const surfOut = g(0);
    surfOut.connect(this.bus);
    this.surfLevel = new Smooth(surfOut.gain, 0);
    const surfMix = g(0);
    surfMix.connect(surfOut);
    this.surfGain = new Smooth(surfMix.gain, 0);
    const surfBand = f('bandpass', 400, 1);
    const surfMakeup = g(9); // band-passed white noise is quiet; bring it up to the thump's level
    const surfAm = g(1);
    const pulse = osc('sawtooth', 40);
    const pulseDepth = g(0);
    pulse.connect(pulseDepth).connect(surfAm.gain);
    const randDepth = g(0);
    this.noise.surfaceRandom.connect(randDepth).connect(surfAm.gain);
    white.connect(surfBand).connect(surfMakeup).connect(surfAm).connect(surfMix);
    const thumpLp = f('lowpass', 420, 0.9); // keep 2nd–5th ridge harmonics: phone speakers barely play < 200 Hz
    const thumpGain = g(0);
    pulse.connect(thumpLp).connect(thumpGain).connect(surfMix);
    this.surfBandHz = new Smooth(surfBand.frequency, 400);
    this.surfQ = new Smooth(surfBand.Q, 1);
    this.surfAmBase = new Smooth(surfAm.gain, 1);
    this.surfPulseHz = new Smooth(pulse.frequency, 40);
    this.surfPulseDepth = new Smooth(pulseDepth.gain, 0);
    this.surfRandDepth = new Smooth(randDepth.gain, 0);
    this.surfRandRate = new Smooth(this.noise.surfaceRandom.playbackRate, this.noise.rateForHz(40));
    this.surfThump = new Smooth(thumpGain.gain, 0);

    // ---- Wind / road.
    const windOut = g(0);
    windOut.connect(this.bus);
    this.windLevel = new Smooth(windOut.gain, 0);
    const windLp = f('lowpass', 400, 0.5);
    const windHp = f('highpass', 110, 0.5);
    const windGainNode = g(0);
    white.connect(windLp).connect(windHp).connect(windGainNode).connect(windOut);
    this.windLpHz = new Smooth(windLp.frequency, 400);
    this.wind = new Smooth(windGainNode.gain, 0);

    // ---- Impacts: pitch-swept thud + band-swept crunch.
    const impOut = g(0);
    impOut.connect(this.bus);
    this.impactLevel = new Smooth(impOut.gain, 0);
    this.impThump = osc('triangle', 120);
    this.impThumpEnv = g(0);
    this.impThump.connect(this.impThumpEnv).connect(impOut);
    this.impBand = f('bandpass', 1500, 0.6);
    this.impCrunchEnv = g(0);
    white.connect(this.impBand).connect(this.impCrunchEnv).connect(impOut);

    for (const o of this.oscillators) o.start();
  }

  /** Call once per rendered frame with the latest sim state. */
  update(s: AudioState, dtIn: number): void {
    if (this.disposed) return;
    const dt = clamp(Number.isFinite(dtIn) ? dtIn : 0, 0, 0.1);
    const t = this.ctx.currentTime;
    const p = audioParams;
    const rpm = clamp(Number.isFinite(s.rpm) ? s.rpm : 4000, 1000, 14000);
    const throttle = clamp(s.throttle || 0, 0, 1);
    const speed = Math.max(0, s.speed || 0);

    if (!this.started) {
      this.started = true;
      this.prevGear = s.gear;
      this.prevSinceShift = s.sinceShift;
    }
    this.masterGain.set(this.muted ? 0 : p.master.value, t, this.muted ? 0.03 : 0.12);

    // ---- Engine.
    this.load = approach(this.load, throttle, dt, 0.035, 0.07);
    const shifted = s.gear !== this.prevGear || s.sinceShift < this.prevSinceShift;
    const upshift = s.gear > this.prevGear;
    if (shifted) this.shift(t, upshift || (s.gear === this.prevGear), throttle);
    this.prevGear = s.gear;
    this.prevSinceShift = s.sinceShift;
    const glide = s.sinceShift < 0.12 ? 0.012 : 0.035;
    this.engine.brightness = p.engineBright.value;
    this.engine.set(rpm * p.enginePitch.value, this.load, t, glide);
    this.engineLevel.set(p.engine.value * 0.55, t, 0.05);

    // Overrun crackle.
    if (throttle < 0.1) this.sinceLift += dt;
    else this.sinceLift = 0;
    const overrun = overrunAmount(rpm, throttle);
    this.popLevel.set(p.pops.value * 0.5, t, 0.05);
    if (overrun > 0.01 && this.rng() < popRate(overrun, this.sinceLift) * dt) {
      this.pop(t + this.rng() * 0.015, (0.35 + 0.65 * this.rng()) * overrun);
    }

    // ---- Turbo.
    this.spool = approach(this.spool, turboTarget(rpm, throttle), dt, 0.45, 0.18);
    const th = turboHz(this.spool);
    this.sWhistleHz.set(th, t, 0.05);
    this.sWhistle.set(0.035 * this.spool * this.spool, t, 0.05);
    this.sHissHz.set(th * 1.5, t, 0.05);
    this.sHiss.set(0.16 * this.spool, t, 0.05);
    this.turboLevel.set(p.turbo.value, t, 0.05);
    if (throttle > 0.6 && this.spool > 0.35) this.bovArmed = true;
    if (this.bovArmed && throttle < 0.2) {
      this.bovArmed = false;
      this.blowOff(t, this.spool);
    }

    // ---- MGU-K.
    const ers = ersGains(s.mguKW || 0, speed);
    const wf = ersWhineHz(speed, p.ersHzPerMs.value);
    this.ersDeployHz.set(wf, t, 0.04);
    this.ersDeploy.set(0.06 * ers.deploy, t, 0.06);
    this.ersHarvestHz.set(wf * 0.68, t, 0.04);
    this.ersHarvestLpHz.set(wf * 1.6, t, 0.04);
    this.ersHarvest.set(0.07 * ers.harvest, t, 0.08);
    this.ersLevel.set(p.ers.value, t, 0.05);

    // ---- Tyres.
    const c = this.squealCurve;
    c.onset = p.squealOnset.value;
    c.slipOnset = p.squealSlipDeg.value * DEG;
    c.slipFull = c.slipOnset * 2;
    const surf = surfaceSquealFactor(s.surface);
    const aF = squealAmount(s.gripFront || 0, s.slipFront || 0, speed, c) * surf;
    const aR = squealAmount(s.gripRear || 0, s.slipRear || 0, speed, c) * surf;
    const base = p.squealPitch.value;
    this.sqFrontHz.set(squealHz(base, speed, aF), t, 0.06);
    this.sqRearHz.set(squealHz(base * 0.8, speed, aR), t, 0.06);
    this.sqFront.set(2.6 * aF, t, 0.035);
    this.sqRear.set(2.9 * aR, t, 0.035);
    this.tyreLevel.set(p.tyres.value, t, 0.05);

    // ---- Surface rumble.
    const sv = surfaceVoice(s.surface, speed, this.sv);
    this.surfGain.set(0.5 * sv.gain, t, 0.025);
    this.surfBandHz.set(sv.bandHz, t, 0.05);
    this.surfQ.set(sv.q, t, 0.05);
    this.surfPulseHz.set(sv.pulseHz, t, 0.03);
    // Inverted sawtooth: sharp hit then decay per ridge.
    this.surfPulseDepth.set(-0.5 * sv.pulseDepth, t, 0.03);
    this.surfRandDepth.set(0.6 * sv.randomDepth, t, 0.03);
    this.surfRandRate.set(this.noise.rateForHz(sv.randomHz), t, 0.05);
    this.surfAmBase.set(Math.max(0.1, 1 - 0.5 * sv.pulseDepth - 0.6 * sv.randomDepth), t, 0.03);
    this.surfThump.set(0.22 * sv.thump, t, 0.03);
    this.surfLevel.set(p.surface.value * 1.6, t, 0.05);

    // ---- Wind.
    this.windLpHz.set(windCutoffHz(speed), t, 0.1);
    this.wind.set(0.1 * windGain(speed), t, 0.1);
    this.windLevel.set(p.wind.value, t, 0.05);

    // ---- Impacts.
    this.impactLevel.set(p.impact.value, t, 0.05);
    const ii = impactIntensity(s.impact || 0);
    if (ii > 0 && (t - this.lastImpactT > 0.15 || ii > this.lastImpactI * 1.5)) {
      this.lastImpactT = t;
      this.lastImpactI = ii;
      this.hit(t, ii);
    }
  }

  /** Ignition cut on upshift (gain dip), throttle blip on downshift, plus a crack. */
  private shift(t: number, up: boolean, throttle: number): void {
    const gp = this.cut.gain;
    cancelHold(gp, t);
    if (up) {
      gp.setTargetAtTime(0.18, t, 0.006);
      gp.setTargetAtTime(1, t + 0.05, 0.014);
      if (throttle > 0.5) this.pop(t + 0.012, 0.55);
    } else {
      gp.setTargetAtTime(1.25, t, 0.01);
      gp.setTargetAtTime(1, t + 0.07, 0.04);
      this.pop(t + 0.03, 0.45 + 0.3 * this.rng());
    }
  }

  private pop(t: number, amp: number): void {
    this.popBand.frequency.setValueAtTime(700 + 1300 * this.rng(), t);
    const e = this.popEnv.gain;
    cancelHold(e, t);
    e.linearRampToValueAtTime(amp, t + 0.0015);
    e.setTargetAtTime(0, t + 0.0015, 0.006 + 0.01 * this.rng());
  }

  private blowOff(t: number, spool: number): void {
    const fp = this.bovBand.frequency;
    cancelHold(fp, t);
    fp.setValueAtTime(1700, t);
    fp.exponentialRampToValueAtTime(450, t + 0.4);
    const e = this.bovEnv.gain;
    cancelHold(e, t);
    e.linearRampToValueAtTime(0.32 * spool, t + 0.012);
    e.setTargetAtTime(0, t + 0.04, 0.11);
  }

  private hit(t: number, i: number): void {
    const fq = this.impThump.frequency;
    cancelHold(fq, t);
    fq.setValueAtTime(150 + 60 * i, t);
    fq.exponentialRampToValueAtTime(55, t + 0.18);
    const te = this.impThumpEnv.gain;
    cancelHold(te, t);
    te.linearRampToValueAtTime(0.9 * i, t + 0.004);
    te.setTargetAtTime(0, t + 0.01, 0.06 + 0.05 * i);
    const bf = this.impBand.frequency;
    cancelHold(bf, t);
    bf.setValueAtTime(2600, t);
    bf.exponentialRampToValueAtTime(600, t + 0.25);
    const ce = this.impCrunchEnv.gain;
    cancelHold(ce, t);
    ce.linearRampToValueAtTime(1.0 * i, t + 0.003);
    ce.setTargetAtTime(0, t + 0.012, 0.05 + 0.07 * i);
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.disposed) return;
    this.masterGain.set(m ? 0 : audioParams.master.value, this.ctx.currentTime, 0.03);
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** Pause all audio processing (page hidden). Only for a realtime AudioContext. */
  suspend(): Promise<void> {
    const ctx = this.ctx as AudioContext;
    if (typeof AudioContext === 'undefined' || !(ctx instanceof AudioContext)) return Promise.resolve();
    return ctx.suspend().catch(() => undefined);
  }

  /**
   * Resume after suspend() or an iOS interruption (phone call, Siri). iOS may
   * refuse outside a user gesture, so also call this from a touch handler when
   * ctx.state !== 'running'.
   */
  resume(): Promise<void> {
    const ctx = this.ctx as AudioContext;
    if (typeof AudioContext === 'undefined' || !(ctx instanceof AudioContext)) return Promise.resolve();
    if (ctx.state === 'running') return Promise.resolve();
    return ctx.resume().catch(() => undefined);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const o of this.oscillators) {
      try {
        o.stop();
      } catch {
        // Already stopped.
      }
      o.disconnect();
    }
    this.engine.dispose();
    this.noise.dispose();
    for (const n of this.owned) n.disconnect();
  }
}

/**
 * iOS: make Web Audio ignore the ring/silent switch (Safari 17+ `navigator.audioSession`).
 * Call in the start gesture, before or right after creating the AudioContext.
 */
export function configureAudioSession(): void {
  try {
    const nav = navigator as Navigator & { audioSession?: { type: string } };
    if (nav.audioSession) nav.audioSession.type = 'playback';
  } catch {
    // Not supported; the silent switch may mute the game on older iOS.
  }
}
